/**
 * useConnections.ts — React Query hooks for Connection management.
 *
 * Used by /connections page + drawers. Cache invalidation strategy:
 *   - List queries invalidate on every mutation
 *   - Detail queries invalidate on their own ID after mutation
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  connectionService,
  type Connection,
  type CreateConnectionInput,
  type UpdateConnectionInput,
  type VendorLookupResult,
} from "../services/connectionService";

export const connectionKeys = {
  all: ["connections"] as const,
  lists: () => [...connectionKeys.all, "list"] as const,
  list: (status?: string) =>
    [...connectionKeys.lists(), { status: status || "all" }] as const,
  details: () => [...connectionKeys.all, "detail"] as const,
  detail: (id: string) => [...connectionKeys.details(), id] as const,
  lookups: () => [...connectionKeys.all, "lookup"] as const,
  lookup: (query: string) => [...connectionKeys.lookups(), query] as const,
};

// ─── Queries ───────────────────────────────────────────────────────

export function useConnections(
  status?: string,
  options?: Omit<UseQueryOptions<Connection[]>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: connectionKeys.list(status),
    queryFn: () => connectionService.list(status),
    staleTime: 30_000,
    ...options,
  });
}

export function useConnection(
  id: string | null,
  options?: Omit<UseQueryOptions<Connection>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: connectionKeys.detail(id || ""),
    queryFn: () => connectionService.get(id!),
    enabled: !!id,
    staleTime: 15_000,
    ...options,
  });
}

export function useVendorLookup(
  query: string,
  options?: Omit<UseQueryOptions<VendorLookupResult>, "queryKey" | "queryFn">,
) {
  // Guard against undefined/null query — the dialog passes "" before any
  // user input which was causing query.trim() to crash.
  const safeQuery = (query ?? "").trim();
  return useQuery({
    queryKey: connectionKeys.lookup(safeQuery),
    queryFn: () => connectionService.lookupVendor(safeQuery),
    enabled: safeQuery.length >= 2,
    staleTime: 60_000,
    ...options,
  });
}

/**
 * Mutation-style vendor lookup for click-to-search UX.
 * Returns a mutate function that takes the query string and resolves to
 * a VendorLookupResult. Used by the NewConnectionDialog where the user
 * types a query and clicks a search button.
 */
export function useVendorLookupMutation() {
  return useMutation({
    mutationFn: (query: string) =>
      connectionService.lookupVendor((query ?? "").trim()),
  });
}

// ─── Mutations ─────────────────────────────────────────────────────

export function useCreateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConnectionInput) =>
      connectionService.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
  });
}

export function useAcceptConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => connectionService.accept(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: connectionKeys.lists() });
      qc.setQueryData(connectionKeys.detail(data._id), data);
    },
  });
}

export function useSuspendConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      connectionService.suspend(id, reason),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: connectionKeys.lists() });
      qc.setQueryData(connectionKeys.detail(data._id), data);
    },
  });
}

export function useReactivateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => connectionService.reactivate(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: connectionKeys.lists() });
      qc.setQueryData(connectionKeys.detail(data._id), data);
    },
  });
}

export function useUpdateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateConnectionInput }) =>
      connectionService.update(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: connectionKeys.lists() });
      qc.setQueryData(connectionKeys.detail(data._id), data);
    },
  });
}

export function useCloseConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      connectionService.close(id, reason),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: connectionKeys.lists() });
      qc.setQueryData(connectionKeys.detail(data._id), data);
    },
  });
}
